<template>
    <div class="animated fadeIn labor-screening personPage withTabs">
        <b-row>
            <b-col sm="9">
                <b-tabs class="labor-screening__tabs" v-model="tabIndex">
                    <b-tab title="Introduction" :disabled="disabledIntroTab" class='introTab'>
                        <b-row>
                            <b-col>
                                <h4 class="labor-screening__header labor-screening__header--center marginB-24">Louisiana Indicator Tool for Child & Youth Labor Trafficking</h4>
                                <p class="labor-screening__description">
                                    The purpose of this tool is to provide indicators to assist in the identification of children and youth who may be victims of labor trafficking.
                                </p>
                                <p class="labor-screening__description">
                                    <b>
                                        Instructions:
                                    </b>
                                    <span>
                                        LA R.S. 14:46.2 states “it shall be unlawful for any person to knowingly recruit, harbor, transport, provide, solicit, receive, isolate, entice, obtain, or maintain the use of another person through fraud, force, or coercion to provide services or labor.” The screening uses indicators that meet the state of Louisiana’s definition of labor trafficking. The result of a completed screening identifies next steps for further monitoring, assessment, and/or reporting. This tool is to be used as a narrative assessment, NOT a questionnaire. The indicators outlined in the tool should not be asked directly to the child or youth. To determine if a child or youth may be a victim of labor trafficking, please check the boxes for any indicators that you note and follow the actions for reporting and referrals in the <b>Conclusion</b> tab.
                                    </span>
                                </p>
                                <p class="labor-screening__description">
                                    <span>The screening results categories are “not enough information," “concern,” and “indicated.” The tool does not include a result of “confirmed” trafficking; a determination that a child or youth is a confirmed victim of human trafficking requires an assessment and validation from a qualified professional. A screening result of “not enough information” does not necessarily mean that the child or youth has not experienced labor trafficking. If there is an indication that the child or youth is currently, was ever, or is in the process of being forced to perform labor or services, you should flag the case for “concern” and follow the actions for additional assessment.
                                        <span class="labor-screening__description--italic">
                                            For the complete instructions, please refer to the
                                            <a
                                                target="_blank"
                                                href="https://lighthouse-pdf-images.s3.us-west-2.amazonaws.com/LT+Identification+Tool+Supplementary+Guide+FINAL.pdf"
                                            >
                                                Supplementary User Guide
                                            </a>.
                                        </span>
                                    </span>
                                </p>
                                <p class="labor-screening__description">For questions regarding this tool, please contact the Governor's Office of Human Trafficking Prevention at <a href="mailto:humantrafficking@la.gov">humantrafficking@la.gov</a>.</p>
                                <!-- <p class="labor-screening__description">
                                    <b>
                                        If the child is in immediate threat of physical harm or danger call 911 immediately.
                                    </b>
                                </p> -->

                                <br>
                                <p class="labor-screening__description"><b>Pre-Screening Criteria</b></p>
                                <p class="labor-screening__description">To ensure that the the Louisiana Child and Youth Labor Trafficking Identification Tool is implemented consistently, select the screening criteria that applies to the child/youth.</p>

                                <div class='prelimDiv' style="paddingLeft: 1.45em">
                                    <b-form-group
                                        :disabled="isLaborScreeningCreated"
                                        label-size='lg'
                                        label-class='bold'
                                        label=''
                                    >
                                        <div 
                                            v-for="[key, value] of Object.entries(prelimFields['Main'])"
                                            :key="key"
                                        >
                                            <b-form-radio
                                                v-model="prelimRadioConditionalModel"
                                                :value='key'
                                                name="prelimMainSelection"
                                                class='radioCheckbox'
                                            >
                                                {{ value.desc }}
                                                <div v-if="key === 'prelim_selective' && laborScreening['prelim_selective'] === true">
                                                    <b-form-checkbox
                                                        size="lg"
                                                        v-for="[sKey, sValue] of Object.entries(prelimFields['Selective'])"
                                                        :key="sKey"
                                                        v-model="laborScreening[sKey]"
                                                        :class="{'bold': sValue.boldDesc}"
                                                    >
                                                        {{ sValue.desc }}
                                                    </b-form-checkbox>
                                                    <span v-show='prelimSelectiveOptionsError && laborScreening[key]' class="text-info">
                                                        Selecting a selective screening criteria is required.
                                                    </span>
                                                </div>

                                                <div v-if="value.type === 'other'">
                                                    <b-form-textarea
                                                        v-if="laborScreening[key]"
                                                        :data-vv-name="value.textVar"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        :id="value.textVar"
                                                        :name="value.textVar"
                                                        class="screening__form-input"
                                                        v-model="laborScreening[value.textVar]"
                                                        :maxlength="255"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has(value.textVar) && laborScreening[key]"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first(value.textVar) }}
                                                    </span>
                                                </div>
                                            </b-form-radio>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div
                                    v-if="!isExisting"
                                    class="labor-screening__button-wrapper button-container"
                                >
                                    <b-button
                                        variant="secondary"
                                        @click="cancelModal"
                                    >Cancel</b-button>
                                    <b-button
                                        variant="primary"
                                        @click="completePrelim"
                                    >Next</b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Screening" :disabled="disabledScreeningTab">
                        <b-row>
                            <b-col cols="12">
                                <b-tabs class="labor-screening__tabs nestedTabs" v-model="screeningTabIndex">
                                    <b-tab title="Ends">
                                        <p class="labor-screening__header labor-screening__header--italic">
                                            The ends of the crime refer to the specific type of victimization that the child or youth is experiencing.
                                        </p>
                                        <p><i>Did the child or youth engage in labor or services against their will, or were they forced to work to pay off debt?</i></p>
                                        <div class="labor-screening__form">
                                            <b-form-group
                                                label-size='lg'
                                                label-class='bold'
                                                label='Forced Labor'
                                                :disabled="isLaborScreeningCreated"
                                            >
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(endsLaborFields['FORCED_LABOR'])"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox size="lg" v-model="laborScreening.e_fl_other">
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.e_fl_other"
                                                        data-vv-name="e_fl_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="e-fl-otherText"
                                                        type='text'
                                                        name="e-fl-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.e_fl_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('e_fl_otherText') && laborScreening.e_fl_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('e_fl_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                            <b-form-group
                                                label-size='lg'
                                                label-class='bold'
                                                label='Debt Bondage'
                                                :disabled="isLaborScreeningCreated"
                                            >
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(endsLaborFields['DEBT_BONDAGE'])"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox size="lg" v-model="laborScreening.e_db_other">
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.e_db_other"
                                                        data-vv-name="e_db_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="e-db-otherText"
                                                        type='text'
                                                        name="e-db-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.e_db_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('e_db_otherText') && laborScreening.e_db_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('e_db_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                        </div>
                                    </b-tab>
                                    <b-tab title="Means">
                                        <p class="labor-screening__header labor-screening__header--italic">
                                            The means refers to the methods of force, fraud, and coercion a perpetrator uses to keep the child or youth in a trafficking situation.
                                        </p>
                                        <p><i>What did the perpetrator do (or threaten to do) to ensure that the child or youth provided labor or services?</i></p>
                                        <div class="labor-screening__form">
                                            <b-form-group
                                                label-size='lg'
                                                label-class='bold'
                                                label='Coercion (psychological, mental, or emotional control)'
                                                :disabled="isLaborScreeningCreated"
                                            >
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(meansLaborFields['COERCION'])"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox size="lg" v-model="laborScreening.m_c_other">
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.m_c_other"
                                                        data-vv-name="m_c_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="m-c-otherText"
                                                        type='text'
                                                        name="m-c-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.m_c_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('m_c_otherText') && laborScreening.m_c_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('m_c_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                            <b-form-group
                                                label-size='lg'
                                                label-class='bold'
                                                label='Fraud (tricked, lied to, or defrauded)'
                                                :disabled="isLaborScreeningCreated"
                                            >
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(meansLaborFields['FRAUD'])"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox size="lg" v-model="laborScreening.m_fr_other">
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.m_fr_other"
                                                        data-vv-name="m_fr_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="m-fr-otherText"
                                                        type='text'
                                                        name="m-fr-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.m_fr_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('m_fr_otherText') && laborScreening.m_fr_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('m_fr_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                            <b-form-group
                                                label-size='lg'
                                                label-class='bold'
                                                label='Force (physical control or restraint)'
                                                :disabled="isLaborScreeningCreated"
                                            >
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(meansLaborFields['FORCE'])"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox size="lg" v-model="laborScreening.m_fo_other">
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.m_fo_other"
                                                        data-vv-name="m_fo_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="m-fo-otherText"
                                                        type='text'
                                                        name="m-fo-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.m_fo_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('m_fo_otherText') && laborScreening.m_fo_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('m_fo_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                        </div>
                                    </b-tab>
                                    <b-tab title="Process">
                                        <p class="labor-screening__header labor-screening__header--italic">
                                            The 'process' refers to the actions the perpetrator takes to compel the child or youth into a trafficking situation.
                                        </p>
                                        <p><i>What were the intentional acts the perpetrator took to get the child or youth in the situation?</i></p>
                                        <div class="labor-screening__form">
                                            <b-form-group :disabled="isLaborScreeningCreated">
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-for="[key, value] of Object.entries(processLaborFields)"
                                                    :key="key"
                                                    v-model="laborScreening[key]"
                                                >
                                                    {{ value.desc }}
                                                </b-form-checkbox>
                                                <b-form-checkbox
                                                    size="lg"
                                                    v-model="laborScreening.p_other"
                                                >
                                                    Other
                                                    <b-form-input
                                                        v-if="laborScreening.p_other"
                                                        data-vv-name="p_otherText"
                                                        v-validate="'required'"
                                                        data-vv-as="Other"
                                                        id="p-otherText"
                                                        type='text'
                                                        name="p-otherText"
                                                        size="lg"
                                                        class="labor-screening__form-input"
                                                        v-model="laborScreening.p_otherText"
                                                    />
                                                    <span
                                                        v-show="inputValidationErrors.has('p_otherText') && laborScreening.p_other"
                                                        class="text-info"
                                                    >
                                                        {{ inputValidationErrors.first('p_otherText') }}
                                                    </span>
                                                </b-form-checkbox>
                                            </b-form-group>
                                        </div>
                                    </b-tab>
                                    <b-tab title="Location">
                                        <p class="labor-screening__header">
                                            Select Your Labor Screening Location and Program:
                                        </p>
                                        <p class="labor-screening__description">
                                            Select the location and program where this Labor Screening occurred.
                                            If location and program are not selected, it will default to the primary location and program of the organization.
                                        </p>
                                        <!-- Added Location -->
                                        <b-form-group
                                            label-size='lg'
                                            label="Location"
                                            label-for="location"
                                            :horizontal='false'
                                        >
                                            <b-form-select
                                                id='location'
                                                v-if="!isLaborScreeningCreated"
                                                :options="locationOptions"
                                                v-model="laborScreening.location"
                                                placeholder='Select a Location'
                                            >
                                            {{ laborScreening.location }}
                                            </b-form-select>
                                            <p v-else class="labor-screening__description">
                                                {{ locationName }}
                                            </p>
                                        </b-form-group>
                                        <!-- Added Program -->
                                        <b-form-group
                                            label-size='lg'
                                            label="Program"
                                            label-for="program"
                                            :horizontal='false'
                                        >
                                            <b-form-select
                                                v-if="!isLaborScreeningCreated"
                                                id='program'
                                                :options="programOptions"
                                                v-model="laborScreening.program"
                                                placeholder='Select a Program'
                                            >
                                                {{ laborScreening.program }}
                                            </b-form-select>
                                            <p v-else class="labor-screening__description">
                                                {{ programName }}
                                            </p>
                                        </b-form-group>
                                    </b-tab>
                                        <div
                                            v-if="!isExisting"
                                            class="labor-screening__button-wrapper button-container padding24"
                                        >
                                            <b-button
                                                variant="secondary"
                                                @click.prevent="cancelModal"
                                            >Cancel</b-button>
                                            <b-button
                                                v-if="showPreviousBtn"
                                                variant="outline-primary"
                                                @click.prevent="prevScreeningTab"
                                            >Previous</b-button>
                                            <b-button
                                                v-if="showNextBtn"
                                                variant="primary"
                                                @click.prevent="nextScreeningTab"
                                            >Next</b-button>
                                            <b-button
                                                v-if="showSubmitBtn"
                                                variant="primary"
                                                @click.prevent="submit"
                                            >Submit</b-button>
                                        </div>
                                </b-tabs>
                            </b-col>
                        </b-row>
                    </b-tab>
                    <b-tab title="Conclusion" :disabled="disabledConclusionTab">
                        <div class='padding24'>
                            <h5 class="labor-screening__header">
                                Summary:
                            </h5>
                            <div class="labor-screening__table">
                                <b-row class="labor-screening__row">
                                    <b-col
                                        class="labor-screening__col"
                                        cols="9"
                                    >
                                        <span>
                                            ENDS
                                        </span>
                                    </b-col>
                                    <b-col
                                        class="labor-screening__col"
                                        cols="3"
                                    >
                                        <b-checkbox :checked="isEndsChecked" disabled />
                                    </b-col>
                                </b-row>
                                <b-row class="labor-screening__row">
                                    <b-col
                                        class="labor-screening__col"
                                        cols="9"
                                    >
                                        <span>
                                            MEANS
                                        </span>
                                    </b-col>
                                    <b-col
                                        class="labor-screening__col"
                                        cols="3"
                                    >
                                        <b-checkbox :checked="isMeansChecked" disabled />
                                    </b-col>
                                </b-row>
                                <b-row class="labor-screening__row">
                                    <b-col
                                        class="labor-screening__col"
                                        cols="9"
                                    >
                                        <span>
                                            PROCESS
                                        </span>
                                    </b-col>
                                    <b-col
                                        class="labor-screening__col"
                                        cols="3"
                                    >
                                        <b-checkbox :checked="isProcessChecked" disabled />
                                    </b-col>
                                </b-row>
                            </div>
                            <div class="labor-screening__conclusion labor-screening__description">
                                <h5 class="labor-screening__description--bold">
                                    Conclusion:
                                </h5>
                                <p :class="laborScreeningConclusionClasses">
                                    {{ LABOR_CONCLUSION_LABEL_MAP[this.laborScreening.conclusion] }}
                                </p>
                                <div v-if="laborScreening.conclusion === LABOR_SCREENING_CONCLUSION.NOT_ENOUGH_INFO">
                                    <!-- <p><b>Not Enough Information</b></p> -->
                                    <p>If there is one checkbox in one of the categories or no boxes checked, there is not enough information to determine the child or youth is a potential victim of labor trafficking.</p>
                                    <p><i>Action to be taken: Continue to monitor the child or youth. Follow mandatory reporting guidelines if you suspect abuse or neglect to a child under 18 years of age by a parent or caretaker. If you suspect a crime has occurred or the child or youth is in immediate danger, report to law enforcement.</i></p>
                                </div>
                                <div v-else-if="laborScreening.conclusion === LABOR_SCREENING_CONCLUSION.LEVEL1">
                                    <!-- <p><b>Level 1: Concern</b></p> -->
                                    <p>If there is at least one checkbox in at least two of the categories, there is concern that the child or youth could potentially be a victim of labor trafficking.</p>
                                    <p><i>Action to be taken: Immediately report to law enforcement if you suspect a crime has occurred or the child or youth is in immediate danger. If you suspect child abuse or neglect to a child under 18 years of age by a parent or caretaker, call the <a href="https://www.dcfs.la.gov/" target="_blank">DCFS</a> Child Abuse/Neglect Hotline at 855-452-5437. Refer to services for additional assessment by contacting the National Human Trafficking Hotline at 888-373-7888 or by referring to <a href="https://humantrafficking.la.gov/resources/" target="_blank">a local service provider</a> with expertise in human trafficking.</i></p>
                                </div>
                                <div v-else-if="laborScreening.conclusion === LABOR_SCREENING_CONCLUSION.LEVEL2">
                                <!-- <p><b>Level 2: Indicated</b></p> -->
                                <p>If there is at least one checkbox in all three categories (Ends, Means, Process), the child or youth meets the state definition of human trafficking and is considered "indicated" for labor trafficking.</p>
                                    <p><i>Action to be taken: Immediately report to law enforcement. If you suspect child abuse or neglect to a child under 18 years of age by a parent or caretaker, call the <a href="https://www.dcfs.la.gov/" target="_blank">DCFS</a> Child Abuse/Neglect Hotline at 855-452-5437. Refer the child to Care Coordination by emailing <a href="mailto:carecoordination@lacacs.org">carecoordination@lacacs.org</a> and attach a copy of the screening tool. (Click Print below.)</i></p>
                                    <p><i>If the child is a non-U.S. citizen or non-lawful permanent resident, report to <a href="https://www.acf.hhs.gov/otip" target="_blank">HHS Office of Trafficking in Persons (OTIP)</a> by submitting a Request for Assistance (RFA) through <a href="https://atims.acf.hhs.gov/eaasidentityserver/Identity/Account/Login/LoginSelection/" target="_blank">Shepherd</a>. (Contact the OTIP Child Protection Specialists by phone at 202-205-4582 or email at <a href="mailto:ChildTrafficking@acf.hhs.gov">ChildTrafficking@acf.hhs.gov</a>.) Also consider referring the child/youth to an attorney to help assess qualifications for legal relief as a potential trafficking victim.</i></p>
                                    <p><i>If the individual is a youth, or aged 18 years or older, provide the individual with the National Human Trafficking Hotline at 888-373-7888 and offer to make a referral to <a href="https://humantrafficking.la.gov/resources/" target="_blank">a local service provider</a> with expertise in human trafficking. If the individual is an adult with a disability, report also to Adult Protective Services at 800-898-4910.</i></p>
                                    <p><i>Update the Victim Status for the child or youth and complete the requested information.</i></p>
                                    <b-button variant="primary" @click="goToVictimStatus">Update Victim Status</b-button>
                                    <p></p>
                                    <p><i>Please note: This tool does not include a result for a confirmed victim of human trafficking. If you receive certification of the child or youth being a confirmed victim of trafficking after completing the screening, you must update their victim status to “confirmed.” A trafficking event is considered “confirmed” when evidence supports the conclusion that the individual is a victim of human trafficking, as defined in La. RS 14:46.2, La. RS 14:46.3, or the federal Trafficking Victims Protection Act (TVPA). While the tool does provide indicators to meet the state legal definition of trafficking, the tool is insufficient alone to weigh the totality of evidence or to replace the assessment of qualified professionals (such as, law enforcement, U.S. Attorney, District Attorney, HHS, victim advocate, Care Coordinator, or other qualified professional.)</i>
                                </p>
                                </div>
                            </div>
                            <p class="labor-screening__description">
                                <b>
                                    Location:
                                </b>
                                {{ locationName }}
                            </p>
                            <p class="labor-screening__description">
                                <b>
                                    Program:
                                </b>
                                {{ programName }}
                            </p>
                        </div>
                    </b-tab>
                </b-tabs>
                <div class='padding24'>
                    <b-button
                        v-if="isLaborScreeningCreated"
                        type="button"
                        variant="primary"
                        class="mb-2 mt-2"
                        size="md"
                        @click="print"
                    >
                        <span class="button-text">
                            Print
                        </span>
                    </b-button>
                </div>
            </b-col>
            <b-col v-if="isLaborScreeningCreated" sm="3">
                <!-- Person Share -->
                <SharePersonList :personId="laborScreening.person._id" :personOrganizationOwner="laborScreening.person.organizationOwner" />
                <!-- :personOrganizationOwner="laborScreening.person.organizationOwner.id -->

                <!--Labor Screening Share Records-->
                <!-- <ShareLaborScreeningList
                    :lsId="laborScreening._id"
                    :friendlyLaborScreeningId="friendlyLaborScreeningId"
                    :organizationOwner="laborScreening.organizationOwner.id"
                    :personId="laborScreening.person._id" 
                /> -->
                <!--Meta-->
                <b-card no-body :title="laborScreening.id">
                    <b-card-header>
                        <b>Meta</b>
                    </b-card-header>
                    <b-card-body>
                        <p class="card-text">
                            <small class="text-muted text-uppercase bold">Labor Screening ID: </small>
                            <small> {{laborScreening.id}} </small>
                        </p>
                        <p class="card-text">
                            <small class="text-muted text-uppercase bold">Person ID</small>
                            <br/>
                            <small>
                            <router-link :to="'/person/' + laborScreening.person.id">
                                {{ laborScreening.person.id }}
                            </router-link>
                            </small>
                        </p>
                    </b-card-body>
                    <b-list-group flush>
                        <b-list-group-item class="small text-muted">
                            <b>Created At: </b> {{ getCreatedAt() }}
                            <div v-if="_.isObject(laborScreening.createdBy)">
                            <b> by: </b> {{ _.get(laborScreening, 'createdBy.emailAddress', '') }}
                            </div>
                            <br>
                            <b>Last Updated: </b> {{ getLastUpdateDate() }}
                            <div v-if="_.isObject(laborScreening.updatedBy)">
                            <b> by: </b> {{ _.get(laborScreening, 'updatedBy.emailAddress', '') }}
                            </div>
                        </b-list-group-item>

                        <b-list-group-item class="small text-muted">
                            <p class="card-text">
                                <small class="text-muted text-uppercase bold">Current Record Owner</small>
                                <br/>
                                <small class="text-muted"><b> by: </b> {{ _.get(laborScreening, 'organizationOwner.title') }} </small>
                            </p>
                        </b-list-group-item>
                    </b-list-group>
                    
                </b-card>
            </b-col>
        </b-row>

        <b-modal
            ref='noSelectionModal'
            v-model="showNoSelectionModal"
            v-bind="{
                'centered': true,
                'title': `Labor Trafficking Screening - ${this.noSelectionSection}`,
                'size': 'lg',
                'ok-title': 'Okay',
                'header-bg-variant': 'success',
                'header-text-variant': 'light',
                'class': 'tos-modal',
                'hide-header-close': true,
                'no-close-on-backdrop': true
            }"
            ok-only
        >
        <template #modal-header-close>
      <h4>X</h4>
    </template>
            <p v-if='this.noSelectionSection === "Introduction"'>Please select the pre-screening criteria that applies to the child/youth prior to continuing with the screening.</p>
            <p v-else>If none of these circumstances apply for the youth, please go back and select the I am not aware of these circumstances applying to the youth/child option.</p>
        </b-modal>

        <b-modal
            ref='cancelModal'
            v-model="showCancelModal"
            v-bind="cancelModalProps"
            @cancel="goBackToPersonRecord"
        >
        <template #modal-header-close>
      <h4>X</h4>
    </template>
            <p>
                {{ cancelModalDescription }}
            </p>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { laborScreening as LaborScreeningModel } from '@/api/models'
import { LABOR_SCREENING_CONCLUSION, LABOR_SCREENING_PREFIX, LABOR_CONCLUSION_LABEL_MAP } from '@/helpers/enums'
import SharePersonList from '@/components/Person/SharePersonList'
// import ShareLaborScreeningList from '@/components/LaborScreening/ShareLaborScreeningList'

const NUM_OF_SCREENING_TABS = 4
const TABS = {
    INTRO: 0,
    SCREENING: 1,
    CONCLUSION: 2
}

export default {
    name: 'AddLaborScreening',

    components: {
        SharePersonList
        // ShareLaborScreeningList
    },

    beforeRouteLeave (to, from, next) {
        if (this.isExisting) {
            next()
            return
        }

        if (this.isLaborScreeningCreated) {
            next()
            return
        }

        if (this.goBackCalled) {
            this.goBackCalled = false
            next()
            return
        }

        this.showCancelModal = true
    },

    inject: ['$validator'],

    props: {
        isExisting: {
            type: Boolean,
            default: false
        }
    },

    data () {
        return {
            tabIndex: TABS.INTRO,
            screeningTabIndex: 0,
            userGuideUrl: '',
            programOptions: [],
            locationOptions: [],
            laborScreening: Object.assign({}, LaborScreeningModel),
            prelimMainSelection: '',
            prelimSelectiveOptionsError: false,
            showNoSelectionModal: false,
            noSelectionSection: 'Introduction',
            showCancelModal: false,
            cancelModalProps: {
                'centered': true,
                'title': 'Cancel Labor Screening',
                'size': 'lg',
                'ok-title': 'No, return to Labor Screening',
                'cancel-title': 'Yes, cancel',
                'header-bg-variant': 'success',
                'header-text-variant': 'light',
                'class': 'tos-modal',
                'hide-header-close': true,
                'no-close-on-backdrop': true
            },
            cancelModalDescription: 'Are you sure you want to cancel this Labor Screening? The information you entered will not be saved.',
            friendlyPersonId: !this.isExisting ? this.$route.params.id : '',
            friendlyLaborScreeningId: this.isExisting ? this.$route.params.id : '',
            goBackCalled: false,
            LABOR_SCREENING_CONCLUSION,
            LABOR_CONCLUSION_LABEL_MAP
        }
    },

    computed: {
        ...mapGetters({
            laborScreeningFields: 'getLaborScreeningFields',
            orgPrograms: 'getProgramsByOrg',
            orgLocations: 'getLocationsByOrg',
            userCurrentContext: 'userCurrentContext',
            person: 'getEditPerson',
            currentLaborScreening: 'getLaborScreening'
        }),

        showPreviousBtn () {
            return this.screeningTabIndex !== 0
        },

        showNextBtn () {
            return this.screeningTabIndex < NUM_OF_SCREENING_TABS - 1
        },

        showSubmitBtn () {
            return this.screeningTabIndex === NUM_OF_SCREENING_TABS - 1
        },

        prelimFields () {
            return this.laborScreeningFields['PRELIM']
        },

        endsLaborFields () {
            return this.laborScreeningFields['ENDS']
        },

        meansLaborFields () {
            return this.laborScreeningFields['MEANS']
        },

        processLaborFields () {
            return this.laborScreeningFields['PROCESS']
        },

        locationName () {
            const selectedLocation = this.laborScreening.location
             if (!selectedLocation && selectedLocation !== 0) {
                return ''
            }
            if (selectedLocation && selectedLocation.id) {
                return selectedLocation.title
            }
            const option = this.locationOptions.filter((loc) => loc.value === selectedLocation)[0]
            return (option && option.text) || ''
        },

        programName () {
            const selectedProgram = this.laborScreening.program
            if (!selectedProgram && selectedProgram !== 0) {
                return ''
            }
            if (selectedProgram && selectedProgram.id) {
                return selectedProgram.title === 'Other' ? selectedProgram.title_otherText : selectedProgram.title
            }
            const option = this.programOptions.filter((program) => program.value === selectedProgram)[0]
            return (option && option.text) || ''
        },

        prelimRadioConditionalModel: {
            // Active form radios need 1 selected name in data, and a completed disabled form needs selection from this.laborScreening
            get () {
                if (!this.isLaborScreeningCreated) {
                    return this.prelimMainSelection
                } else {
                    return Object.keys(this.prelimFields['Main']).find(k => this.laborScreening[k] === true)
                }
            },
            set (newValue) {
                if (!this.isLaborScreeningCreated) {
                    this.prelimMainSelection = newValue
                }
            }
        },
        checkPrelimSelectiveOptions () {
            if (this.laborScreening['prelim_selective']) {
                return Object.keys(this.laborScreening)
                    .filter((key) => Object.keys(this.prelimFields['Selective']).includes(key))
                    .reduce((isChecked, key) => isChecked || !!this.laborScreening[key], false)
            } else {
                return true
            }
        },
        checkPrelim () {
            if (this.laborScreening['prelim_other']) {
                return this.laborScreening['prelim_other_text'].length > 0
            } else {
                return this.areFieldsChecked(LABOR_SCREENING_PREFIX.PRELIM)
            }
        },

        isEndsChecked () {
            return this.areFieldsChecked(LABOR_SCREENING_PREFIX.ENDS)
        },

        isMeansChecked () {
            return this.areFieldsChecked(LABOR_SCREENING_PREFIX.MEANS)
        },

        isProcessChecked () {
            return this.areFieldsChecked(LABOR_SCREENING_PREFIX.PROCESS)
        },

        isLaborScreeningCreated () {
            return !!this.laborScreening._id && !!this.laborScreening.id
        },

        disabledIntroTab () {
            return !this.isExisting && (
                this.isLaborScreeningCreated || this.tabIndex > TABS.INTRO
            )
        },

        disabledScreeningTab () {
            return !this.isExisting && (
                this.isLaborScreeningCreated || (
                    (this.tabIndex <= TABS.INTRO &&
                    this.tabIndex >= TABS.CONCLUSION) ||
                    (!this.checkPrelim || !this.checkPrelimSelectiveOptions)
                )
            )
        },

        disabledConclusionTab () {
            return !this.isExisting && !this.isLaborScreeningCreated
        },

        laborScreeningConclusionClasses () {
            return [
                'mb-0',
                'labor-screening__description',
                'labor-screening__description--bold',
                {
                    'text-warning': this.laborScreening.conclusion === LABOR_SCREENING_CONCLUSION.LEVEL1,
                    'text-danger': this.laborScreening.conclusion === LABOR_SCREENING_CONCLUSION.LEVEL2
                }
            ]
        }
    },

    updated () {
        // Reset prelim selective options error to false when 1 is checked
        if (this.checkPrelimSelectiveOptions) {
            this.prelimSelectiveOptionsError = false
        }
    },

    watch: {
        currentLaborScreening (newValue) {
            if (newValue) {
                this.laborScreening = newValue
                this.checkLaborScreeningOtherFields()
            }
        },

        prelimMainSelection (newValue) {
            Object.keys(this.prelimFields['Main']).filter(e => e !== newValue).forEach((k) => {
                this.laborScreening[k] = false
                if (k.includes('_other')) {
                    this.laborScreening[`${k}_text`] = ''
                }
            })
            this.laborScreening[newValue] = true

            if (newValue !== 'prelim_selective') {
                Object.keys(this.prelimFields['Selective']).forEach(k => { this.laborScreening[k] = false })
                this.prelimSelectiveOptionsError = false
            }
        },

        orgPrograms (newValue) {
            if (newValue) {
                this.setProgramOptions()
                this.setDefaultProgram()
            }
        },

        orgLocations (newValue) {
            if (newValue) {
                this.setLocationOptions()
                this.setDefaultLocation()
            }
        }
    },

    async created () {
        const orgId = _.get(this.userCurrentContext, 'organization.id', false)

        if (this.isExisting) {
            console.log('friendlyLaborScreeningId', this.friendlyLaborScreeningId)
            await this.$store.dispatch('getLaborScreening', {
                id: this.friendlyLaborScreeningId
            })
            this.tabIndex = TABS.CONCLUSION
            return
        }

        this.getOrgProgramsAndLocations(orgId)

        const groupId = _.get(this.userCurrentContext, 'group.id', false)
        const userId = _.get(this.userCurrentContext, 'userMember.muser', false)

        if (this.friendlyPersonId) {
            await this.$store.dispatch('getPersonForEdit', {
                id: this.friendlyPersonId,
                orgId: orgId,
                groupId: groupId
            })
        }
        this.initLaborScreening(userId, orgId)
    },

    beforeDestroy () {
        this.$store.dispatch('initCreateLaborScreening')
    },

    methods: {
        getCreatedAt () {
            const laborScreening = this.laborScreening
            if (laborScreening.createdAt && laborScreening.createdAt.includes('T')) {
                return this.$utils.formatDate(laborScreening.createdAt)
            } else if (laborScreening.createdAt) {
                return this.$utils.unixToHuman(laborScreening.createdAt / 1000)
            }
        },
        getLastUpdateDate () {
            const laborScreening = this.laborScreening
            if (laborScreening.updatedAt && laborScreening.updatedAt.includes('T')) {
                return this.$utils.formatDate(laborScreening.updatedAt)
            } else if (laborScreening.updatedAt) {
                return this.$utils.unixToHuman(laborScreening.updatedAt / 1000)
            }
        },
        goToVictimStatus (item) {
            console.log(this.laborScreening.person)
            this.$router.push({ path: '/person/' + this.laborScreening.person.id + '/edit/victiminformation' })
        },
        areFieldsChecked (prefix) {
            return Object.keys(this.laborScreening)
                .filter((key) => key.startsWith(prefix))
                .reduce((isChecked, key) => isChecked || !!this.laborScreening[key], false)
        },

        goBackToPersonRecord () {
            this.showCancelModal = false
            this.goBackCalled = true
            this.$router.push({
                path: `/person/${this.friendlyPersonId}`
            })
        },

        setProgramOptions () {
            const programs = this.orgPrograms.map((program) => {
                return {
                    text: program.title === 'Other'
                        ? program.title_otherText
                        : program.title,
                    value: program.id
                }
            })
            programs.sort((a, b) => a.text.localeCompare(b.text))
            this.programOptions = programs
        },

        setLocationOptions () {
            const locations = this.orgLocations.map((location) => {
                return {
                    text: location.title === 'Other'
                        ? location.title_otherText
                        : location.title,
                    value: location.id
                }
            })
            locations.sort((a, b) => a.text.localeCompare(b.text))
            this.locationOptions = locations
        },

        setDefaultLocation () {
            const location = this.orgLocations[0]
            const locationId = _.get(this.userCurrentContext, 'organization.defaultLocation', false) ||
                (location && location.id)
            this.laborScreening.location = locationId
        },

        setDefaultProgram () {
            const program = this.orgPrograms[0]
            const programId = _.get(this.userCurrentContext, 'organization.defaultProgram', false) ||
                (program && program.id)
            this.laborScreening.program = programId
        },

        getOrgProgramsAndLocations (orgId) {
            this.$store.dispatch('getLocationsByOrg', { orgId })
            this.$store.dispatch('getProgramsByOrg', { orgId })
        },

        checkLaborScreeningOtherFields () {
            const checkedOtherFields = Object.keys(this.laborScreening).filter((key) => key.endsWith('otherText') && !!this.laborScreening[key])

            checkedOtherFields.forEach((field) => {
                const key = field.substring(0, field.length - 4)
                this.laborScreening[key] = true
            })
        },

        initLaborScreening (userId, orgId) {
            delete this.laborScreening._id
            delete this.laborScreening.id
            delete this.laborScreening.createdAt
            delete this.laborScreening.updatedAt

            this.laborScreening.createdBy = userId
            this.laborScreening.updatedBy = userId
            this.laborScreening.organizationCreator = orgId
            this.laborScreening.organizationOwner = orgId
            this.laborScreening.person = this.person._id
        },

        completePrelim () {
            this.$validator.validate().then(isValidated => {
                if (isValidated) {
                    if (this.checkPrelim) {
                        if (this.checkPrelimSelectiveOptions) {
                            this.prelimSelectiveOptionsError = false
                            this.tabIndex += 1
                            window.scrollTo(0, 0)
                        } else {
                            this.prelimSelectiveOptionsError = true
                        }
                    } else {
                        this.noSelectionSection = 'Introduction'
                        this.showNoSelectionModal = true
                    }
                }
            })
        },

        nextScreeningTab () {
            this.$validator.validate()
                .then((isValidated) => {
                    if (isValidated) {
                        this.screeningTabIndex += 1
                        window.scrollTo(0, 0)
                    }
                })
        },

        prevScreeningTab () {
            this.screeningTabIndex -= 1
        },

        cancelModal () {
            this.showCancelModal = true
        },

        _any (obj) {
            return Object.keys(obj)
                .reduce((isAny, key) => isAny || key, false)
        },

        calcConclusion () {
            const checkedSections = [
                this.isEndsChecked,
                this.isMeansChecked,
                this.isProcessChecked
            ]

            const checkedCount = checkedSections.filter((section) => section).length
            if (checkedCount <= 1) {
                return LABOR_SCREENING_CONCLUSION.NOT_ENOUGH_INFO
            }

            return checkedCount === 2
                ? LABOR_SCREENING_CONCLUSION.LEVEL1
                : LABOR_SCREENING_CONCLUSION.LEVEL2
        },

        async submit () {
            if (!this.isLaborScreeningCreated && this.laborScreening) {
                // remove all the excess other fields
                this.laborScreening = Object.keys(this.laborScreening)
                    .reduce((obj, key) => {
                        if (key.endsWith('other') && key !== 'prelim_other') {
                            delete obj[key]
                        }
                        return obj
                    }, this.laborScreening)
                this.laborScreening.conclusion = this.calcConclusion()
                await this.$store.dispatch('createLaborScreening', this.laborScreening)
                this.$router.push(`/laborscreening/${this.laborScreening.id}/view/conclusion`)
            }
        },

        print () {
            if (!this.isLaborScreeningCreated) {
                return
            }

            this.$router.push({
                path: `/laborScreening/${this.laborScreening.id}/print`
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.labor-screening {
    &__header {
        &--italic {
           font-style: italic;
        }

        &--center {
            text-align: center;
        }

        &--underline {
            text-decoration: underline;
        }
    }

    &__description {
        &--bold {
            font-weight: 700;
        }

        &--italic {
            font-style: italic;
        }
    }

    &__button-wrapper {
        display: block;
        text-align: right;
    }

    &__form {
        padding: 10px;
    }

    &__form-input {
        margin-left: 2px;
        width: auto;
        display: block;
    }

    &__table {
        padding: 0 0 20px 20px;
        width: 200px;
    }

    &__row {
        border: 1px solid #000000;
    }

    &__col {
        &.col-3 {
            border-left: 1px solid #000000;
        }
    }

    &__wrapper {
        padding: 0 15px;
    }
}

::v-deep .custom-checkbox.b-custom-control-lg .custom-control-label::before {
    top: 50% !important;
}

.prelimDiv .radioCheckbox {
    font-size: 1.25em;
    line-height: 1.75em;
}
.prelimDiv .custom-checkbox {
    font-size: 1em;
    line-height: 1.5em;
}
</style>


// WEBPACK FOOTER //
// src/views/t1/Admin/LaborScreening.vue